import React from "react";
import { Controller, Control, RegisterOptions, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText, Popper } from "@material-ui/core";
import styled from "styled-components";
import { createFilterOptions, useAutocomplete } from "@material-ui/lab";
import { FontStyles, Tag } from "../../index";
import { ExpandMore } from "@material-ui/icons";
import { fonts } from "../../../config";

const InputWrapper = styled.div(({ theme, $error }: any) => ({
  ...FontStyles.medium14,
  display: "flex",
  alignItems: "center",
  gap: "4px",
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: theme.palette.primary.contrastText,
  borderRadius: 21,
  height: 48,
  cursor: "text",
  overflow: "hidden",
}));

const TagsArea = styled.div(() => ({
  display: "flex",
  flexWrap: "wrap" as const,
  alignContent: "flex-start",
  gap: "4px",
  flex: 1,
  overflowY: "auto" as const,
  height: "100%",
}));

const SearchContainer = styled.div(({ theme }: any) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider || "#e0e0e0"}`,
}));

const SearchInput = styled.input(({ theme }: any) => ({
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  color: theme.palette.text.primary,
  fontFamily: fonts.medium,
  fontSize: fonts.sizes.regular,
  boxShadow: "transparent 0px 0px",
  "&::placeholder": { opacity: 0.8 },
}));

const Label = styled.p(({ theme, $witherror, white }: any) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.semibold14,
    textTransform: "capitalize",
    padding: "4px",
    margin: 0,
    marginBottom: "2vh",
    color: $witherror
      ? colors.error.main
      : white
        ? "white"
        : colors.action.selected,
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
    lineHeight: FontStyles.semibold14.fontSize,
  };
});

const ErrorMessage = styled(FormHelperText)(({ theme }: any) => {
  const { palette: colors } = theme;
  return { color: colors.text.secondary };
});

const LabelView = styled.div(() => ({
  display: "flex",
  alignContent: "center",
  justifyContent: "flex-start",
}));

const ListboxContainer = styled.ul(({ theme }: any) => {
  const { palette: colors } = theme;
  return {
    margin: 0,
    padding: "4px 0",
    listStyle: "none",
    backgroundColor: colors.primary.contrastText,
    maxHeight: 250,
    overflowY: "auto" as const,
    color: colors.text.primary,
    fontFamily: fonts.medium,
    fontSize: fonts.sizes.regular,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    borderRadius: "0 0 8px 8px",
    "& li": {
      padding: "8px 16px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      '&[data-focus="true"]': { backgroundColor: colors.action.focus + "20" },
    },
    "::-webkit-scrollbar-thumb": {
      background: colors.primary.contrastText,
      borderRadius: "15px",
      height: "2px",
    },
  };
});

const PopperContainer = styled.div(({ theme }: any) => ({
  backgroundColor: theme.palette.primary.contrastText,
  borderRadius: 8,
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  overflow: "hidden",
}));

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1300,
}));

export interface InputTagsRHFProps {
  control: Control<any>;
  name: string;
  label?: string;
  options: Array<{ label: string; value: string | number }>;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  rules?: RegisterOptions;
  onChangeCustom?: (value: any) => void;
  placeholder?: string;
  white?: boolean;
  noOptionsText?: boolean;
}

// Inner component so useAutocomplete is called at the top level of a React component
function InputTagsInner({
  field,
  fieldState,
  options,
  multiple = false,
  disabled,
  readOnly,
  onChangeCustom,
  placeholder = "",
  white,
  label,
  name,
  noOptionsText,
}: any) {
  const formContext = useFormContext();
  const [all, setAll] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const popperInputRef = React.useRef<HTMLInputElement>(null);
  const popperContainerRef = React.useRef<HTMLDivElement>(null);

  const filterOptionsDefault = createFilterOptions({
    stringify: (option: any) => option?.label,
  });

  const filterOptions = (opts: any[], params: any) => {
    return filterOptionsDefault(opts, params);
  };

  const value: any[] = Array.isArray(field.value) ? field.value : [];

  const RemoveDuplicates = (list: any[]) => {
    const valueArr = list.map((item) => item?.value);
    const isDuplicate = valueArr.some(
      (item, idx) => valueArr.indexOf(item) !== idx
    );
    if (!isDuplicate) return list;
    const toRemove = list
      .map((e) => e["value"])
      .map((e, i, final) => (final.indexOf(e) !== i ? i : false))
      .filter((obj): obj is number => obj !== false)
      .map((e) => list[e]["value"]);
    return list.filter((el) => el?.value !== toRemove[0]);
  };

  const removeAllValue = (array: any[]) =>
    array.filter((item) => item?.value !== "all");

  const handleOptions = (opts: any[], isMultiple: boolean) => {
    if (isMultiple && opts.length > 1) return [{ label: "Todos", value: "all" }, ...opts];
    return [...opts];
  };

  const {
    getRootProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    popupOpen,
  } = useAutocomplete({
    id: `tags-${name}`,
    multiple: true, // always array-based internally; single mode handled in onChange
    open: isOpen,
    onOpen: () => setIsOpen(true),
    value,
    inputValue: searchText,
    onInputChange: (_: any, newVal: string) => setSearchText(newVal),
    options: handleOptions(options, multiple),
    filterOptions: filterOptions,
    getOptionLabel: (option: any) => option?.label || "",
    disableCloseOnSelect: multiple,
    clearOnBlur: false,
    clearOnEscape: false,
    disabled: readOnly || disabled,
    onChange: (event: any, selectedValue: any[]) => {
      let newValue: any[];
      if (multiple) {
        const haveAll = RemoveDuplicates(selectedValue)?.find(
          (i: any) => i?.value === "all"
        );
        if (all && !haveAll) {
          setAll(false);
          newValue = [];
        } else if (selectedValue === null || selectedValue?.length < 1) {
          newValue = RemoveDuplicates(selectedValue ?? []);
        } else if (haveAll && !all) {
          setAll(true);
          newValue = RemoveDuplicates([
            { value: "all", label: "Todos" },
            ...options,
          ]);
        } else {
          setAll(false);
          newValue = RemoveDuplicates(removeAllValue(selectedValue));
        }
      } else {
        // single mode: take only the newly added item
        const newItem = selectedValue.find(
          (sv: any) => !value.find((p: any) => p.value === sv.value)
        );
        newValue = newItem ? [newItem] : [];
      }
      field.onChange(newValue);
      if (onChangeCustom) onChangeCustom(newValue);
      if (fieldState.invalid) formContext?.trigger(name);
    },
    onClose: () => {
      setIsOpen(false);
      setSearchText("");
      field.onBlur();
      formContext?.trigger(name);
    },
  } as any);

  React.useEffect(() => {
    if (isOpen) setTimeout(() => popperInputRef.current?.focus(), 0);
  }, [isOpen]);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (popperContainerRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearchText("");
      field.onBlur();
      formContext?.trigger(name);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const inputRefCallback = (el: HTMLInputElement | null) => {
    const { ref } = getInputProps() as any;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
    (inputRef as any).current = el;
  };

  const errorMsg = fieldState.error?.message;
  const displayTags = value.filter((v: any) => v?.value !== "all");

  return (
    <FormControl fullWidth error={Boolean(errorMsg)}>
      <LabelView>
        <Label white={white} $witherror={Boolean(errorMsg) ? true : undefined}>
          {label}
        </Label>
      </LabelView>

      <InputWrapper
        ref={wrapperRef}
        $error={Boolean(errorMsg)}
        {...getRootProps()}
        onClick={() => {
          if (!isOpen && !readOnly && !disabled) {
            setIsOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <TagsArea>
          {displayTags.map((option: any, index: number) => {
            const tagProps = getTagProps({ index }) as any;
            const TagAny = Tag as any;
            return (
              <TagAny
                key={option.value}
                label={option.label}
                {...tagProps}
                onDelete={readOnly || disabled ? undefined : tagProps.onDelete}
              />
            );
          })}
        </TagsArea>
        <input
          {...getInputProps()}
          ref={inputRefCallback}
          onBlur={() => { }}
          onFocus={() => { setTimeout(() => popperInputRef.current?.focus(), 0); }}
          style={{ position: "absolute", opacity: 0, width: 0, height: 0, padding: 0, border: 0 }}
        />
        <ExpandMore
          style={{ color: "#4D585A", flexShrink: 0, paddingRight: "4px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (readOnly || disabled) return;
            if (isOpen) {
              setIsOpen(false);
            } else {
              setIsOpen(true);
            }
          }}
        />
      </InputWrapper>

      {popupOpen && (
        <StyledPopper
          open
          anchorEl={wrapperRef.current}
          placement="bottom-start"
          style={{ width: wrapperRef.current ? wrapperRef.current.clientWidth : undefined }}
        >
          <PopperContainer ref={popperContainerRef}>
            <SearchContainer>
              <SearchInput
                ref={popperInputRef}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(getInputProps() as any).onKeyDown}
                placeholder="Pesquisar..."
              />
            </SearchContainer>
            <ListboxContainer {...getListboxProps()}>
              {(groupedOptions as any[]).length > 0 ? (
                (groupedOptions as any[]).map((option: any, index: number) => {
                  const isSelected = value?.find(
                    (i: any) => i?.value === option?.value
                  );
                  const { key: optionKey, ...optionProps } = getOptionProps({ option, index }) as any;
                  return (
                    <li key={optionKey ?? option.value} {...optionProps}>
                      <div
                        style={{
                          backgroundColor: "#FFFFFF00",
                          border: "2px solid #24272A",
                          borderRadius: "5px",
                          height: "18px",
                          width: "18px",
                          marginRight: "4px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: isSelected ? "#24272A" : "transparent",
                            borderRadius: "2px",
                            height: "8px",
                            width: "8px",
                          }}
                        />
                      </div>
                      {option?.label}
                    </li>
                  );
                })
              ) : !noOptionsText ? (
                <li style={{ padding: "8px 16px", pointerEvents: "none" }}>
                  Sem opções
                </li>
              ) : null}
            </ListboxContainer>
          </PopperContainer>
        </StyledPopper>
      )}

      {Boolean(errorMsg) && (
        <ErrorMessage error={true}>{errorMsg}</ErrorMessage>
      )}
    </FormControl>
  );
}

function InputTagsRHF({
  control,
  name,
  label,
  options,
  multiple = false,
  disabled,
  readOnly,
  rules,
  onChangeCustom,
  placeholder = "",
  white,
  noOptionsText,
}: InputTagsRHFProps) {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <InputTagsInner
          field={field}
          fieldState={fieldState}
          options={options}
          multiple={multiple}
          disabled={disabled}
          readOnly={readOnly}
          onChangeCustom={onChangeCustom}
          placeholder={placeholder}
          white={white}
          label={label}
          name={name}
          noOptionsText={noOptionsText}
        />
      )}
    />
  );
}

export default InputTagsRHF;
